import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import '../../../styles/unified.css';

const sections = [
  {
    id: 1,
    title: 'Acceptance of Terms',
    content: [
      'By accessing or using BipzApp (the "Platform"), you confirm that you have read, understood, and agree to be legally bound by these Terms of Service ("Terms"). These Terms form a binding agreement between you and BipzApp, the company that owns and operates this Platform.',
      'If you are using the Platform on behalf of a driving school, instructor organisation, or any other business entity, you represent that you have the authority to bind that entity to these Terms.',
      'If you do not agree with any part of these Terms, you must discontinue use of the Platform immediately.',
    ],
  },
  {
    id: 2,
    title: 'About BipzApp',
    content: [
      'BipzApp is a cloud-based driving school management platform designed to help driving instructors and schools manage student records, schedule and book lessons, track student progress, capture and track payments, and communicate with learners — all in one place.',
      'The Platform is accessible via web browser and is supported by a range of cloud services for reliability, security, and performance. Features may be updated, extended, or removed from time to time as the Platform continues to evolve.',
      'BipzApp is intended for use by driving instructors, driving school administrators, and their students. Use of the Platform for any other purpose is not permitted without prior written approval from BipzApp.',
    ],
  },
  {
    id: 3,
    title: 'User Accounts & Access',
    content: [
      'To use BipzApp, you must register for an account. You agree to provide accurate, current, and complete information during registration and to keep your account details up to date at all times.',
      'You are solely responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You must notify us immediately if you suspect any unauthorised access to your account.',
      'We reserve the right to suspend or permanently deactivate any account that is found to be in breach of these Terms, involved in fraudulent activity, or otherwise used in a manner inconsistent with the intended purpose of the Platform.',
      'Student accounts are created and managed by the instructor or school administrator. Students are responsible for keeping their own login information secure and must not share access with others.',
    ],
  },
  {
    id: 4,
    title: 'Booking & Lesson Management',
    content: [
      'BipzApp enables instructors to create lesson slot templates, manage bookings, and track student progress over time. Instructors are solely responsible for the accuracy of the lesson details, availability, and any information they publish through the Platform.',
      'Bookings confirmed through BipzApp represent a scheduling agreement between the instructor and the student. BipzApp facilitates this process but is not a party to the agreement and accepts no liability for missed lessons, cancellations, or disputes arising from individual bookings.',
      'Automated scheduling tools, reminders, and reconciliation features are provided as a convenience. Instructors should independently verify their schedules and not rely solely on automated processes for critical time-sensitive operations.',
    ],
  },
  {
    id: 5,
    title: 'Payments & Fees',
    content: [
      'BipzApp may facilitate the recording and tracking of payments between instructors and students. Any payment collection features are tools to assist instructors in managing their own fee arrangements and do not constitute a financial service provided by BipzApp.',
      'BipzApp is not responsible for payment disputes, refunds, or financial losses arising from transactions between instructors and students. All financial agreements are entered into directly between those parties.',
     /* "Access to BipzApp is currently provided free of charge during the platform's growth phase. We reserve the right to introduce subscription plans or usage-based pricing in the future. Users will be given reasonable advance notice of any changes to pricing before they take effect.",
      'Any future billing will be handled securely. BipzApp will never store full payment card details on its own servers.',*/
    ],
  },
  {
    id: 6,
    title: 'Data Privacy & Security',
    content: [
      'We take the privacy and security of your data seriously. BipzApp collects and processes personal data — including student records, contact information, lesson history, and usage data — in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.',
      "Data collected through the Platform is used solely for the purpose of providing and improving BipzApp's services. We do not sell, rent, or share personal data with third parties for marketing purposes.",
      'By using BipzApp, instructors acknowledge that they act as data controllers for the student data they input into the Platform. Instructors are responsible for ensuring they have appropriate lawful basis for processing student personal data.',
      'Our full Privacy Policy is available separately and forms part of this agreement. We encourage all users to read it carefully.',
    ],
  },
  {
    id: 7,
    title: 'Acceptable Use',
    content: [
      'You agree to use BipzApp only for lawful purposes and in accordance with these Terms. You must not use the Platform in any way that violates applicable local, national, or international laws or regulations.',
      'Prohibited activities include, but are not limited to: attempting to gain unauthorised access to any part of the Platform or its infrastructure; uploading or transmitting any content that is harmful, abusive, defamatory, or otherwise objectionable; reverse engineering, decompiling, or disassembling any part of the Platform; using automated tools to scrape, extract, or harvest data from the Platform without prior written consent.',
      'We reserve the right to investigate any suspected breach of acceptable use and to take appropriate action, including suspension of access and reporting to relevant authorities where required by law.',
    ],
  },
  {
    id: 8,
    title: 'Intellectual Property',
    content: [
      'All intellectual property rights in BipzApp — including but not limited to its software, design, branding, logo, content, and underlying technology — are owned exclusively by BipzApp or its licensors.',
      'You are granted a limited, non-exclusive, non-transferable right to access and use the Platform solely for its intended purpose. This licence does not transfer any ownership rights to you.',
      'Any feedback, suggestions, or ideas you share with us regarding the Platform may be used by BipzApp without obligation or compensation to you, and we may incorporate such input into the Platform at our discretion.',
    ],
  },
  {
    id: 9,
    title: 'Limitation of Liability',
    content: [
      'To the fullest extent permitted by applicable law, BipzApp shall not be liable for any indirect, incidental, special, or consequential loss or damage arising from your use of or inability to use the Platform, even if we have been advised of the possibility of such damage.',
      'BipzApp is provided on an "as available" basis. While we strive for high availability, we do not warrant that the Platform will be uninterrupted, error-free, or free from technical issues. We will endeavour to provide advance notice of planned maintenance where possible.',
      'Our total cumulative liability to you in connection with BipzApp, for any reason and regardless of the form of action, shall not exceed the total fees you have paid to us in the twelve months preceding the claim.',
    ],
  },
  {
    id: 10,
    title: 'Modifications to These Terms',
    content: [
      'We may update these Terms from time to time to reflect changes in the Platform, our business practices, or applicable law. When we make material changes, we will notify users via email or through a notice displayed within the Platform.',
      'The date of the most recent revision will be clearly indicated at the top of this page. Your continued use of BipzApp after a revision has been published signifies your acceptance of the updated Terms.',
      'If you disagree with any revised Terms, you have the right to close your account and discontinue use of the Platform prior to the changes taking effect.',
    ],
  },
  {
    id: 11,
    title: 'Termination',
    content: [
      'You may stop using BipzApp and close your account at any time by contacting us. Upon account closure, your data will be retained for a period in accordance with our data retention policy and applicable legal obligations, after which it will be securely deleted.',
      'We reserve the right to suspend or terminate your access to BipzApp at any time if we determine, in our sole discretion, that you have violated these Terms, engaged in conduct harmful to other users, or acted against the best interests of the Platform.',
      'Termination of your account does not affect any rights or obligations that have accrued prior to termination, including any outstanding payment obligations.',
    ],
  },
  {
    id: 12,
    title: 'Governing Law',
    content: [
        '-TBD-'
      /*
      'These Terms and any dispute or claim arising out of or in connection with them shall be governed by and construed in accordance with the laws of England and Wales.',
      'Both parties agree to submit to the exclusive jurisdiction of the courts of England and Wales to resolve any dispute or legal proceedings arising in relation to these Terms or your use of BipzApp.',
      'If any provision of these Terms is found to be invalid or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect.',
    */
    ],
  },
];

export default function Terms() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();
  const handleBack = () => navigate(isAuthenticated ? '/dashboard' : '/');

  return (
    <div className="terms-page">

      {/* Hero */}
      <div className="terms-hero">
        <div className="terms-hero-bg" />
        <div className="terms-hero-content">
          <button className="static-back-btn" onClick={handleBack}>
            ← {isAuthenticated ? 'Back to Dashboard' : 'Back to Home'}
          </button>
          <h1 className="terms-title">Terms of Service</h1>
          <p className="terms-subtitle">
            Please read these terms carefully before using BipzApp. They set out your rights,
            responsibilities, and our commitments to you.
          </p>
          <div className="terms-meta">
            <span className="terms-meta-item">
              <span className="terms-meta-icon">📅</span>
              Last updated: 8 May 2025
            </span>
            <span className="terms-meta-divider" />
            <span className="terms-meta-item">
              <span className="terms-meta-icon">🏢</span>
              BipzApp
            </span>
            <span className="terms-meta-divider" />
            <span className="terms-meta-item">
              <span className="terms-meta-icon">⚖️</span>
              England & Wales
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="terms-container">

        <div className="terms-intro-box">
          <p>
            These Terms of Service govern your access to and use of <strong>BipzApp</strong>, a
            driving school management platform. By creating an account or using the Platform in
            any way, you agree to the terms set out below. Questions? Reach us at{' '}
            <a href="mailto:support@bipzapp.com" className="terms-link">
              support@bipzapp.com
            </a>
            .
          </p>
        </div>

        {sections.map((section) => (
          <div key={section.id} id={`section-${section.id}`} className="terms-section">
            <div className="terms-section-header">
              <span className="terms-section-num">{section.id}</span>
              <h2 className="terms-section-title">{section.title}</h2>
            </div>
            <div className="terms-section-body">
              {section.content.map((para, i) => (
                <p key={i} className="terms-para">
                  {para}
                </p>
              ))}
            </div>
          </div>
        ))}

        {/* Acknowledgement */}
        <div className="terms-acknowledgement">
          <div className="terms-ack-icon">✓</div>
          <p>
            By creating or continuing to use a BipzApp account, you acknowledge that you have
            read and understood these Terms of Service and agree to be bound by them.
          </p>
        </div>

        {/* Contact CTA */}
        <div className="terms-contact-box">
          <h3>Have a question about these Terms?</h3>
          <p>
            If anything in this document is unclear or you have concerns about how these Terms
            apply to you, please don't hesitate to get in touch.
          </p>
          <a href="mailto:support@bipzapp.com" className="terms-contact-btn">
            Contact Us
          </a>
        </div>

      </div>
    </div>
  );
}